import { findLesson, type LessonSummary } from './manifest'
import { parseLesson, type LessonBlock, type LessonFrontMatter } from './directiveParser'

/**
 * Lesson Loader
 * -------------
 * Resolves a lesson id to its static Markdown and parses it into blocks. Lesson
 * files are imported at build time via `import.meta.glob` — there is no runtime
 * API and no network fetch. Author-only material (notes, sources) is separated
 * from the learner-facing blocks here, at the boundary.
 */

// Eagerly bundle every lesson's raw Markdown, keyed by filename.
const rawLessons = import.meta.glob('../../content/lessons/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>

const byFilename = new Map<string, string>()
for (const [path, source] of Object.entries(rawLessons)) {
  const filename = path.split('/').pop()
  if (filename) byFilename.set(filename, source)
}

export interface LoadedLesson {
  summary: LessonSummary
  meta: LessonFrontMatter
  /** Learner-facing content only (prose + directives). */
  blocks: LessonBlock[]
  /** Author-only note, kept out of the rendered lesson. */
  authorNotes?: string
  /** Author-only sources, kept out of the rendered lesson. */
  sources?: string[]
}

export function getLessonSummary(id: string): LessonSummary | undefined {
  return findLesson(id)
}

export function loadLesson(id: string): LoadedLesson {
  const summary = findLesson(id)
  if (!summary) {
    throw new Error(`No lesson "${id}" in the content manifest.`)
  }

  if (!summary.file) {
    throw new Error(`Lesson "${id}" is in the curriculum but not yet published.`)
  }

  const source = byFilename.get(summary.file)
  if (source === undefined) {
    throw new Error(`Lesson file "${summary.file}" for "${id}" was not found in content/lessons.`)
  }

  const { meta, blocks } = parseLesson(source)
  return {
    summary,
    meta,
    blocks,
    authorNotes: meta.notes,
    sources: meta.sources,
  }
}
